import React, { useState } from 'react';
import Form, { GroupItem, SimpleItem, ButtonItem, RequiredRule, Label, PatternRule } from 'devextreme-react/form';
import { FormWrapperProps } from '@/core/interfaces/usestate-interfaces';
import { t } from 'i18next';
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import { ButtonType } from 'devextreme/common';
import { BUTTON_CONSTANTS } from '@/core/constants/common/button-contants';
import { LoadPanel } from 'devextreme-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import HtmlEditor, { Item, Toolbar } from 'devextreme-react/html-editor';


const colCountByScreen = {
    xs: 2,
    sm: 2,
    md: 2,
    lg: 2,
};

const FormWrapper: React.FC<FormWrapperProps> = ({
    formRef,
    submitFormRef,
    formData,
    simpleItemGroups = [],
    showValidationSummary = false,
    validationGroup = 'validationGroup',
    readOnly = false,
    buttonGroups = [],
    backButtonNavigationPath,
    isAddBackBtn = true,
    cardEnable = true,
    onContinue,
    onSubmit,
    onOptionChanged,
    addNewRecord,
    gotoNext
}) => {

    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const navigate = useNavigate();

    const formSubmit = async (e?: any, goToBackAfterSubmit: boolean = false) => {
        e && e.preventDefault();
        if (onSubmit) {
            setLoadPanelVisible(true);
            onSubmit()
                .then(async () => {
                    toast.success(t("saved_changes"));
                    if (goToBackAfterSubmit)
                        goToBack();
                    else
                        onContinue && await onContinue();
                })
                .catch((error) => {
                    toast.error(error.response.data.message);
                })
                .finally(() => {
                    setLoadPanelVisible(false);
                })
        }

    }

    const goToBack = () => {
        if (backButtonNavigationPath)
            navigate(backButtonNavigationPath)
        else
            navigate(-1)
    }

    const backButtonOptions = {
        elementAttr: {
            [ELEMENT_ID]: "backButton"
        },
        text: t('back'),
        type: 'normal' as ButtonType,
        useSubmitBehavior: false,
        with: '120px',
        icon: 'back',
        onClick: goToBack
    };

    const saveButtonOptions = {
        elementAttr: { [ELEMENT_ID]: "registerButton" },
        text: t('save'),
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '120px'
    };

    const newRecordButtonOptions = {
        elementAttr: {
            [ELEMENT_ID]: "newRecordButton"
        },
        text: t('new_record'),
        type: 'default' as ButtonType,
        width: '120px',
        onClick: addNewRecord
    };

    const saveAndContinueButtonOptions = {
        elementAttr: { [ELEMENT_ID]: "saveButton" },
        text: t('saveAndContinue'),
        type: 'default' as ButtonType,
        useSubmitBehavior: false,
        width: '190px',
        onClick: async () => {
            if (formRef.current) {
                const validationResult = formRef.current.instance.validate();
                if (validationResult.isValid) {
                    await formSubmit(undefined, false);

                }
            }
        }
    };

    const nextButtonOptions = {
        text: t('next'),
        type: 'normal' as ButtonType,
        width: '120px',
        onClick: gotoNext
    }

    const chooseButtonOption = (btnOption: any): any => {
        let buttonOption = btnOption;
        switch (btnOption) {
            case BUTTON_CONSTANTS.SaveBtn:
                buttonOption = saveButtonOptions;
                break;
            case BUTTON_CONSTANTS.SaveAndContinueBtn:
                buttonOption = saveAndContinueButtonOptions;
                break;
            case BUTTON_CONSTANTS.NewRecordBtn:
                buttonOption = newRecordButtonOptions;
                break;
            case BUTTON_CONSTANTS.NextBtn:
                buttonOption = nextButtonOptions;
                break;
            case BUTTON_CONSTANTS.BackBtn:
                buttonOption = backButtonOptions;
                break;
            default:
                buttonOption = btnOption;
                break;
        }
        return buttonOption
    }

    const getForm = () => {
        return <Form
            ref={formRef}
            formData={formData}
            readOnly={readOnly}
            showColonAfterLabel={true}
            showValidationSummary={showValidationSummary}
            validationGroup={validationGroup}
            onOptionChanged={onOptionChanged}
        >
            {buttonGroups &&
                <GroupItem
                    cssClass={'d-flex justify-content-end'}
                    colCount={buttonGroups.length + (isAddBackBtn ? 1 : 0)}>
                    {isAddBackBtn && (
                        <ButtonItem
                            buttonOptions={backButtonOptions}
                        />
                    )}
                    {buttonGroups.map((button: any, buttonIndex: number) => {
                        const btnOption = chooseButtonOption(button.buttonOptions);
                        return (
                            <ButtonItem
                                key={buttonIndex}
                                buttonOptions={btnOption}
                            />
                        );
                    })}
                </GroupItem>
            }
            {simpleItemGroups.map((group: any, groupIndex: number) => (
                <GroupItem key={groupIndex}
                    {...(group.useColCountByScreen ? { colCountByScreen: colCountByScreen } : {})} caption={group.caption}>
                    {group.items.map((item: any, itemIndex: number) => (
                        <SimpleItem
                            key={itemIndex}
                            dataField={item.dataField}
                            cssClass={item.cssClass}
                            editorType={item.editorType}
                            editorOptions={item.editorOptions}
                            visible={item.visible}
                        >
                            <Label text={t(item.label)} />
                            {item.isRequired && <RequiredRule message={t(item.requiredMessage)} />}
                            {item.pattern && <PatternRule message={t(item.patternMessage)} pattern={item.pattern} />}
                        </SimpleItem>
                    ))}
                </GroupItem>
            ))}
        </Form>
    }
    return (

        <form id="content-layout" ref={submitFormRef} onSubmit={formSubmit} style={{ height: "100%" }} >
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={t("saving_please_wait")}

            />
            {cardEnable &&
                <div className='card mb-5 mb-xl-10'>
                    <div id='account_definition' className='collapse show'>
                        <div className='card-body border-top p-5'>
                            {getForm()}
                        </div>
                    </div>
                </div>
            }
            {!cardEnable &&
                getForm()
            }

        </form>

    );
};

export default FormWrapper;
